import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabaseClient'
import type { EventRow, EventStatus } from '@/lib/types'
import { Badge, Button, Card, EmptyState, Field, Input, Modal, PageSpinner, Select, Textarea, useToast } from '@/components/ui'
import { formatDate } from '@/lib/format'

const STATUS_TONE: Record<EventStatus, 'slate' | 'green' | 'amber' | 'blue'> = {
  draft: 'slate',
  active: 'green',
  closed: 'amber',
  archived: 'slate',
}

export default function EventsList() {
  const [events, setEvents] = useState<EventRow[] | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const navigate = useNavigate()
  const { push } = useToast()

  async function load() {
    const { data, error } = await supabase.from('events').select('*').order('created_at', { ascending: false })
    if (error) push(error.message, 'error')
    setEvents((data as EventRow[]) || [])
  }

  useEffect(() => {
    load()
  }, [])

  if (events === null) return <PageSpinner label="Loading events…" />

  return (
    <div className="mx-auto max-w-5xl px-6 py-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-slate-900">Events</h1>
          <p className="text-sm text-slate-500">Every hotel allocation, company and rooming list is scoped to one event.</p>
        </div>
        <Button variant="primary" onClick={() => setShowCreate(true)}>
          + New Event
        </Button>
      </div>

      {events.length === 0 ? (
        <EmptyState
          title="No events yet"
          description="Create your first conference/event to start allocating hotel rooms to companies."
          action={
            <Button variant="primary" onClick={() => setShowCreate(true)}>
              Create Event
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {events.map((ev) => (
            <Card key={ev.id} className="cursor-pointer px-4 py-4 hover:border-brand-300" onClick={() => navigate(`/admin/events/${ev.id}`)}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-medium text-slate-900">{ev.name}</div>
                  <div className="text-xs text-slate-500">{ev.code}</div>
                </div>
                <Badge tone={STATUS_TONE[ev.status]}>{ev.status}</Badge>
              </div>
              <div className="mt-3 text-xs text-slate-500">
                {ev.venue ? `${ev.venue}, ` : ''}
                {ev.city || ''}
              </div>
              <div className="mt-1 text-xs text-slate-400">
                {ev.start_date ? formatDate(ev.start_date) : 'No dates set'}
                {ev.end_date ? ` – ${formatDate(ev.end_date)}` : ''}
              </div>
            </Card>
          ))}
        </div>
      )}

      <CreateEventModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(id) => {
          setShowCreate(false)
          navigate(`/admin/events/${id}`)
        }}
      />
    </div>
  )
}

function CreateEventModal({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: (id: string) => void }) {
  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [venue, setVenue] = useState('')
  const [city, setCity] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [description, setDescription] = useState('')
  const [status, setStatus] = useState<EventStatus>('draft')
  const [saving, setSaving] = useState(false)
  const { push } = useToast()

  async function submit() {
    if (!name.trim() || !code.trim()) {
      push('Name and code are required', 'error')
      return
    }
    setSaving(true)
    const { data, error } = await supabase
      .from('events')
      .insert({
        name: name.trim(),
        code: code.trim(),
        venue: venue.trim() || null,
        city: city.trim() || null,
        start_date: startDate || null,
        end_date: endDate || null,
        description: description.trim() || null,
        status,
      })
      .select('id')
      .single()
    setSaving(false)
    if (error) {
      push(error.message, 'error')
      return
    }
    push('Event created')
    onCreated(data!.id as string)
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New Event"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={saving} onClick={submit}>
            Create Event
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Event Name" required>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="EFRE Conference 2027" />
        </Field>
        <Field label="Event Code" required hint="Short unique code, used internally.">
          <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="EFRE2027" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Start Date">
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </Field>
          <Field label="End Date">
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Venue">
            <Input value={venue} onChange={(e) => setVenue(e.target.value)} />
          </Field>
          <Field label="City">
            <Input value={city} onChange={(e) => setCity(e.target.value)} />
          </Field>
        </div>
        <Field label="Status">
          <Select value={status} onChange={(e) => setStatus(e.target.value as EventStatus)}>
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="closed">Closed</option>
            <option value="archived">Archived</option>
          </Select>
        </Field>
        <Field label="Description">
          <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
      </div>
    </Modal>
  )
}
